import React, { useState, useEffect, useRef, useCallback } from "react";
import { getAuthToken } from "./api";
import toast from "react-hot-toast";
import useSubmissionSafety from "./hooks/useSubmissionSafety";

const API_BASE = process.env.REACT_APP_API_BASE || "https://mockbot-api1.onrender.com";

// ── Data ─────────────────────────────────────────────────────────────────────

const SECTIONS_META = [
  { id: "technical", label: "Technical",  count: 20, color: "#667eea", colorDim: "rgba(102,126,234,0.18)" },
  { id: "aptitude",  label: "Aptitude",   count: 30, color: "#0e7c66", colorDim: "rgba(14,124,102,0.18)" },
  { id: "workstyle", label: "Workstyle",  count: 10, color: "#c99a2e", colorDim: "rgba(201,154,46,0.18)" },
];

// Fallback metadata for question sets that don't use the technical/aptitude/
// workstyle split (e.g. a company-supplied flat MCQ bank, tagged "general").
function sectionMeta(id) {
  const known = SECTIONS_META.find((s) => s.id === id);
  if (known) return known;
  const label = id ? id.charAt(0).toUpperCase() + id.slice(1) : "Questions";
  return { id: id || "general", label, color: "#667eea", colorDim: "rgba(102,126,234,0.18)" };
}

// Section list actually present in `questions`, preserving the canonical
// technical/aptitude/workstyle order when present, so existing LLM-generated
// (institutional/practice) sessions render byte-identical to before.
function deriveSectionsMeta(questions) {
  const present = new Set(questions.map((q) => q.section || "general"));
  const known = SECTIONS_META.filter((s) => present.has(s.id));
  const extra = [...present].filter((id) => !SECTIONS_META.some((s) => s.id === id)).map(sectionMeta);
  return known.length > 0 || extra.length > 0 ? [...known, ...extra] : [sectionMeta("general")];
}

const INTERVIEW_CATEGORIES = [
  { value: "generic", label: "General",          sub: "Open-ended practice",      description: "Role-focused assessment for any domain.",              focus: "Adaptive questions" },
  { value: "service", label: "Service Industry", sub: "TCS, Infosys, Wipro…",     description: "Technical + aptitude for service company hiring.",     focus: "Execution readiness" },
  { value: "big4",    label: "Big 4",            sub: "Deloitte, PwC, EY, KPMG",  description: "Case-led + analytical + workstyle for consulting.",    focus: "Consulting polish" },
];

const DIFFICULTY_OPTIONS = [
  { value: "Beginner",     note: "Entry level" },
  { value: "Intermediate", note: "2–5 yrs" },
  { value: "Advanced",     note: "5+ yrs" },
  { value: "Expert",       note: "Staff / Lead" },
];

const EXP_QUICK = ["0", "1", "2", "3", "5", "7", "10"];

const ASSESSMENT_TIPS = [
  { num: "01", h: "Technical — 20 questions",  p: "Role-specific coding, algorithms, system design, and tool knowledge, tailored to your JD." },
  { num: "02", h: "Aptitude — 30 questions",   p: "Numerical reasoning, logical patterns, and verbal ability across three equal sub-sections." },
  { num: "03", h: "Workstyle — 10 questions",  p: "Professional preference and situational scenarios to assess your team and work style." },
  { num: "04", h: "Fresh every attempt",        p: "LLM generates a completely new set on each run — no two tests are identical." },
  { num: "05", h: "Instant scorecard",          p: "Section-wise scores, answer feedback and skill bars appear after you submit." },
];

const SERVICE_COMPANIES = [
  { value: "TCS",        full: "Tata Consultancy Services",         note: "Mass hiring focused",           pattern: "Aptitude → Technical → HR",   focus: ["Aptitude", "Coding", "Project Discussion"], recommended: true },
  { value: "Infosys",    full: "Infosys Limited",                   note: "Service-based engineering",     pattern: "Online test → Tech → HR",     focus: ["DSA Basics", "SQL", "Behavioral"] },
  { value: "Capgemini",  full: "Capgemini SE",                      note: "Client project interviews",     pattern: "Pseudo-code → Tech → HR",     focus: ["Pseudo-code", "OOP", "Communication"] },
  { value: "Accenture",  full: "Accenture plc",                     note: "Consulting-leaning engineering", pattern: "Cognitive → Tech → HR",       focus: ["Cognitive", "Coding", "Comms"], recommended: true },
  { value: "Cognizant",  full: "Cognizant Technology Solutions",    note: "Digital engineering",           pattern: "Online → Tech → Final",       focus: ["DSA", "DBMS", "Behavioral"] },
  { value: "Wipro",      full: "Wipro Limited",                     note: "Large-scale onboarding",        pattern: "Aptitude → Tech → Business",  focus: ["Logical", "Verbal", "Coding"] },
  { value: "HCLTech",    full: "HCL Technologies",                  note: "Engineering services",          pattern: "Tech → Managerial → HR",      focus: ["Networking", "OS", "Project Fit"] },
  { value: "TechMahindra", full: "Tech Mahindra Limited",           note: "Telecom & digital",             pattern: "Aptitude → Tech → HR",        focus: ["Aptitude", "Tech Stack", "Comms"] },
];

const BIG4_FIRMS = [
  { value: "Deloitte", practices: "Consulting · Audit & Assurance · Risk Advisory · Tax",   focus: ["Case Study", "Business Problem Solving", "Stakeholder Communication", "Analytical Thinking"], recommended: true },
  { value: "PwC",      practices: "Advisory · Assurance · Tax · Deals",                      focus: ["Market Sizing", "Case Study", "Client Comms", "Logical Reasoning"] },
  { value: "EY",       practices: "Consulting · Strategy · Tax · Assurance",                focus: ["Guesstimate", "Frameworks", "Stakeholder Mgmt", "Quant Analysis"] },
  { value: "KPMG",     practices: "Advisory · Tax · Audit",                                  focus: ["Case Math", "Hypothesis Testing", "Exec Presence", "Issue Tree"] },
];

const LOADING_MSGS = [
  "Generating technical questions…",
  "Building aptitude challenges…",
  "Crafting workstyle scenarios…",
  "Calibrating difficulty levels…",
  "Finalising your test set…",
];

// ── Shared helpers ────────────────────────────────────────────────────────────

function DonutRing({ pct, color, size = 38, stroke = 3.5 }) {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(100, Math.max(0, pct)) / 100);
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={stroke}
        strokeDasharray={`${circ}`} strokeDashoffset={`${offset}`}
        strokeLinecap="round"
        style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
      />
    </svg>
  );
}

function CategoryIcon({ type }) {
  if (type === "generic") return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" width="18" height="18">
      <circle cx="10" cy="10" r="8" /><path d="M10 6v4l3 3" strokeLinecap="round" />
    </svg>
  );
  if (type === "service") return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" width="18" height="18">
      <rect x="3" y="7" width="14" height="10" rx="2" /><path d="M7 7V5a3 3 0 016 0v2" strokeLinecap="round" />
    </svg>
  );
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" width="18" height="18">
      <path d="M4 4l4 4-4 4M10 16h6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TechIcon() {
  return (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.7" width="16" height="16">
      <polyline points="5,8 2,11 5,14" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="13,8 16,11 13,14" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="10" y1="4" x2="8" y2="18" strokeLinecap="round" />
    </svg>
  );
}

function AptIcon() {
  return (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.7" width="16" height="16">
      <rect x="2" y="10" width="3" height="6" rx="1" />
      <rect x="7" y="6" width="3" height="10" rx="1" />
      <rect x="12" y="2" width="3" height="14" rx="1" />
    </svg>
  );
}

function WorkIcon() {
  return (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.7" width="16" height="16">
      <circle cx="9" cy="5" r="3" />
      <path d="M2 16c0-3.3 3.1-6 7-6s7 2.7 7 6" strokeLinecap="round" />
    </svg>
  );
}

const SECTION_ICONS = { technical: <TechIcon />, aptitude: <AptIcon />, workstyle: <WorkIcon /> };

function SparkIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2z" />
    </svg>
  );
}

// ── Config phase sub-components (identical structure to SetupScreen) ───────────

function AssessmentInfoPanel() {
  return (
    <section className="setup-panel">
      <div className="setup-panel__head">
        <span className="setup-panel__eyebrow">How it works</span>
        <h2>60 questions, three sections</h2>
        <p>A timed assessment covering technical knowledge, aptitude, and work-style preferences. Questions are AI-generated fresh on every attempt.</p>
      </div>
      <ol className="setup-guide__list">
        {ASSESSMENT_TIPS.map((step) => (
          <li key={step.num} className="setup-guide__item">
            <span className="setup-guide__num">{step.num}</span>
            <div className="setup-guide__body">
              <div className="setup-guide__h">{step.h}</div>
              <p className="setup-guide__p">{step.p}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="setup-guide__foot">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" />
        </svg>
        <span>Need company-specific questions? Switch to <strong>Service Industry</strong> or <strong>Big 4</strong> above.</span>
      </div>
    </section>
  );
}

function ServicePanel({ selectedCompany, onSelect }) {
  const [query, setQuery] = useState("");
  const filtered = query.trim()
    ? SERVICE_COMPANIES.filter((c) =>
        (c.value + " " + c.full + " " + c.note).toLowerCase().includes(query.toLowerCase())
      )
    : SERVICE_COMPANIES;

  return (
    <section className="setup-panel">
      <div className="setup-panel__head">
        <span className="setup-panel__eyebrow">Service Industry</span>
        <h2>Pick a company to target</h2>
        <p>Technical and aptitude questions calibrated to each company's hiring pattern and difficulty level.</p>
      </div>
      <div className="co-search-wrap">
        <svg className="co-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
        </svg>
        <input className="form-input" style={{ paddingLeft: "2.2rem" }}
          placeholder="Search companies…" value={query}
          onChange={(e) => setQuery(e.target.value)} />
      </div>
      <div className="company-card-grid">
        {filtered.map((co) => (
          <button key={co.value} type="button"
            className={`co-card${selectedCompany === co.value ? " is-on" : ""}`}
            onClick={() => onSelect(co.value)}>
            {co.recommended && <span className="co-card__rec"><SparkIcon /> Recommended</span>}
            <div className="co-card__top">
              <span className="co-card__mark">{co.value.slice(0, 1)}</span>
              <div className="co-card__id">
                <div className="co-card__name">{co.value}</div>
                <div className="co-card__full">{co.full}</div>
              </div>
              <span className="co-card__radio" />
            </div>
            <div className="co-card__tag">{co.note}</div>
            <div className="co-card__pattern">{co.pattern}</div>
            <div className="co-card__focus">
              {co.focus.map((f) => <span key={f} className="co-focus-chip">{f}</span>)}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function Big4Panel({ selectedFirm, onSelect }) {
  return (
    <section className="setup-panel">
      <div className="setup-panel__head">
        <span className="setup-panel__eyebrow">Big 4 Consulting</span>
        <h2>Pick a firm to target</h2>
        <p>Case-led analytical questions with workstyle scenarios adapted to each firm's assessment philosophy.</p>
      </div>
      <div className="b4-grid">
        {BIG4_FIRMS.map((f) => (
          <button key={f.value} type="button"
            className={`b4-card${selectedFirm === f.value ? " is-on" : ""}`}
            onClick={() => onSelect(f.value)}>
            {f.recommended && <span className="b4-card__rec"><SparkIcon /> Recommended</span>}
            <div className="b4-card__top">
              <span className="b4-card__mark">{f.value.slice(0, 1)}</span>
              <div className="b4-card__id">
                <div className="b4-card__name">{f.value}</div>
                <div className="b4-card__sub">{f.practices}</div>
              </div>
              <span className="b4-card__radio" />
            </div>
            <div className="b4-card__divider" />
            <div className="b4-card__focus-h">Assessment focus</div>
            <div className="b4-card__chips">
              {f.focus.map((fa) => <span key={fa} className="focus-chip">{fa}</span>)}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ── Phase 1: Config ───────────────────────────────────────────────────────────

function AssessmentConfig({ onStart }) {
  const [track, setTrack] = useState("generic");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [role, setRole] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [difficulty, setDifficulty] = useState("Beginner");
  const [jobDescFile, setJobDescFile] = useState(null);
  const [jdText, setJdText] = useState("");
  const [jdInputMode, setJdInputMode] = useState("upload");

  const adjustExp = (delta) => {
    const current = parseInt(yearsExperience) || 0;
    setYearsExperience(String(Math.max(0, Math.min(30, current + delta))));
  };

  const categoryCopy = INTERVIEW_CATEGORIES.find((item) => item.value === track);

  const handleStart = () => {
    if (!role.trim()) { toast.error("Role is required"); return; }
    onStart({
      role: role.trim(),
      experience: yearsExperience || "0",
      difficulty,
      category: track,
      company: selectedCompany,
      jdText,
    });
  };

  return (
    <div className="setup-screen">
      <div className="setup-product-shell">

        {/* Header — identical structure to SetupScreen */}
        <div className="setup-header setup-header--product">
          <span className="setup-eyebrow">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            Assessment Test
          </span>
          <h1 className="setup-title">Aptitude & MCQ Assessment</h1>
          <p className="setup-subtitle">Practice role-based aptitude and technical assessments with AI-generated questions and instant scoring.</p>
        </div>

        {/* Track tabs — identical to SetupScreen */}
        <div className="setup-track-tabs">
          {INTERVIEW_CATEGORIES.map((item) => (
            <button key={item.value} type="button"
              className={`setup-track-tab${track === item.value ? " is-on" : ""}`}
              onClick={() => { setTrack(item.value); setSelectedCompany(""); }}>
              <span className="setup-track-tab__icon"><CategoryIcon type={item.value} /></span>
              <span className="setup-track-tab__copy">
                <strong>{item.label}</strong>
                <span>{item.sub}</span>
              </span>
            </button>
          ))}
        </div>

        {/* Two-column grid — identical to SetupScreen */}
        <div className="setup-product-grid">

          <main className="setup-main-flow">
            {track === "generic" && <AssessmentInfoPanel />}
            {track === "service" && <ServicePanel selectedCompany={selectedCompany} onSelect={setSelectedCompany} />}
            {track === "big4"    && <Big4Panel    selectedFirm={selectedCompany}   onSelect={setSelectedCompany} />}
          </main>

          <aside className="setup-rail">
            <div className="setup-rail__sticky">
              <section className="setup-config-card">
                <div className="setup-config-card__head">
                  <span>Test Config</span>
                  <strong>{categoryCopy?.label}</strong>
                </div>

                {/* Role — same as SetupScreen */}
                <div className="form-group">
                  <label htmlFor="as-role">Role <span className="field-req">*</span></label>
                  <input type="text" id="as-role" value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g., Software Engineer, PM, Data Analyst"
                    className="form-input" />
                </div>

                {/* Experience — same stepper as SetupScreen */}
                <div className="form-group">
                  <label>Experience</label>
                  <div className="exp-stepper">
                    <button type="button" className="exp-btn" onClick={() => adjustExp(-1)}>-</button>
                    <input type="number" value={yearsExperience}
                      onChange={(e) => setYearsExperience(e.target.value)}
                      min="0" max="30" placeholder="0"
                      className="form-input exp-input" />
                    <span className="exp-unit">yrs</span>
                    <button type="button" className="exp-btn" onClick={() => adjustExp(1)}>+</button>
                  </div>
                  <div className="exp-chips">
                    {EXP_QUICK.map((v) => (
                      <button key={v} type="button"
                        className={`exp-chip${yearsExperience === v ? " is-on" : ""}`}
                        onClick={() => setYearsExperience(v)}>{v}</button>
                    ))}
                  </div>
                </div>

                {/* Difficulty — same pills as SetupScreen */}
                <div className="form-group">
                  <label>Difficulty</label>
                  <div className="diff-pills">
                    {DIFFICULTY_OPTIONS.map(({ value, note }) => (
                      <button key={value} type="button"
                        className={`diff-pill${difficulty === value ? " is-on" : ""}`}
                        onClick={() => setDifficulty(value)}>
                        <span className="diff-pill__label">{value}</span>
                        <span className="diff-pill__note">{note}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Job Description — same as SetupScreen (no resume needed) */}
                <div className="form-group">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                    <label style={{ margin: 0 }}>Job Description <span className="field-opt">optional</span></label>
                    <div className="jd-mode-tabs jd-mode-tabs--compact">
                      <button type="button" className={jdInputMode === "upload" ? "is-on" : ""} onClick={() => setJdInputMode("upload")}>Upload</button>
                      <button type="button" className={jdInputMode === "paste"  ? "is-on" : ""} onClick={() => setJdInputMode("paste")}>Paste</button>
                    </div>
                  </div>
                  {jdInputMode === "upload" ? (
                    <label htmlFor="as-jobdesc" className="file-upload-btn">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 16V3m0 0l-5 5m5-5l5 5" /><path d="M20 21H4" />
                      </svg>
                      <span>{jobDescFile ? jobDescFile.name : "Upload JD"}</span>
                      <input type="file" id="as-jobdesc" accept=".pdf,.docx,.txt"
                        onChange={(e) => setJobDescFile(e.target.files[0] || null)}
                        style={{ display: "none" }} />
                    </label>
                  ) : (
                    <textarea className="form-input jd-textarea" value={jdText}
                      onChange={(e) => setJdText(e.target.value)}
                      placeholder="Paste the job description here. Questions will be tailored to it."
                      rows={4} style={{ resize: "vertical" }} />
                  )}
                </div>

                {/* CTA — same class as SetupScreen, different text */}
                <button onClick={handleStart} className="start-button">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  Generate &amp; Start Test
                </button>

                <p className="setup-legal">60 AI-generated questions · scored automatically</p>
              </section>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ── Phase 2: Generating ───────────────────────────────────────────────────────

function GeneratingScreen() {
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setMsgIdx((i) => (i + 1) % LOADING_MSGS.length), 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="atest-generating">
      <div className="atest-generating__inner">
        <div className="atest-generating__ring">
          {[0, 1, 2].map((i) => (
            <div key={i} className="atest-generating__dot" style={{ animationDelay: `${i * 0.22}s` }} />
          ))}
        </div>
        <p className="atest-generating__title">Preparing your assessment</p>
        <p className="atest-generating__msg">{LOADING_MSGS[msgIdx]}</p>
        <p className="atest-generating__hint">This takes 20–40 seconds. Please don't close the page.</p>
      </div>
    </div>
  );
}

// ── Phase 3: Test ─────────────────────────────────────────────────────────────


function QuestionCard({ q, qNum, answer, onAnswer, bookmarked, onBookmark }) {
  const secMeta = sectionMeta(q.section);
  return (
    <div className={`atest-q${answer ? " atest-q--answered" : ""}`} id={`q-${q.id}`}>
      <div className="atest-q__header">
        <span className="atest-q__num">Q{qNum}</span>
        {secMeta && (
          <span className="atest-q__sect" style={{ color: secMeta.color, background: secMeta.colorDim, border: `1px solid ${secMeta.color}55` }}>
            {secMeta.label}
          </span>
        )}
        {q.topic && <span className="atest-q__topic">{q.topic}</span>}
        <span className={`atest-q__diff atest-q__diff--${q.difficulty}`}>{q.difficulty}</span>
        <button type="button" className={`atest-bookmark${bookmarked ? " is-on" : ""}`} onClick={onBookmark} title="Bookmark">
          <svg viewBox="0 0 16 16" fill={bookmarked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6" width="13" height="13">
            <path d="M3 2h10v12l-5-3-5 3V2z" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <p className="atest-q__text">{q.text}</p>
      <div className="atest-opts">
        {["A", "B", "C", "D"].map((key) => (
          <button key={key} type="button"
            className={`atest-opt${answer === key ? " is-on" : ""}`}
            onClick={() => onAnswer(key)}>
            <span className="atest-opt__key">{key}</span>
            <span className="atest-opt__text">{q.options[key]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function AssessmentTest({
  questions, onSubmit, campaignId = null, isOnline = true, deadlineEpoch = null,
  hideSubmitUntilLast = false,
  // Epoch ms of the last time a recruiter granted this candidate a retake
  // (null if never). A saved draft older than this is from BEFORE the
  // retake — i.e. the previous, already-scored attempt — and must not be
  // restored into what's supposed to be a fresh test.
  minDraftTimestamp = null,
}) {
  const SETS = Math.ceil(questions.length / 10);
  const sectionsMeta = deriveSectionsMeta(questions);
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState({});
  const [bookmarks, setBookmarks] = useState(new Set());
  // When a recruiter-configured deadline exists, the VISIBLE countdown is
  // derived from it directly instead of a separate local counter — they used
  // to be two independent values (this one always starting at a hardcoded
  // 3600s/60min, deadlineEpoch enforcing whatever the recruiter actually
  // configured, e.g. 110 minutes), so the candidate saw a 60-minute timer
  // and the test auto-submitted at 60 minutes regardless of what was
  // actually configured. Falls back to the legacy fixed 60-minute countdown
  // only when no deadlineEpoch is set at all (jobs that never configured a
  // total test time).
  const [timeLeft, setTimeLeft] = useState(() =>
    deadlineEpoch ? Math.max(0, Math.round((deadlineEpoch - Date.now()) / 1000)) : 3600
  );
  const mainRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showMissedConfirm, setShowMissedConfirm] = useState(false);

  const handleSubmitAll = useCallback(() => {
    if (submitted) return;
    setSubmitted(true);
    onSubmit(answers);
  }, [answers, onSubmit, submitted]);

  // Only the manual Submit button goes through this confirmation — auto-
  // submit on the per-question timer or the global deadline calls
  // handleSubmitAll directly, since there's no one to confirm with once
  // time is up.
  //
  // This used to be a window.confirm(), but that's a native browser dialog —
  // showing one forces an exit from fullscreen as an unavoidable side
  // effect. Re-requesting fullscreen right after only works if the browser
  // still considers it a fresh user gesture, which isn't guaranteed once a
  // candidate takes more than a couple of seconds to read the dialog and
  // click OK — so the re-request could silently fail, leaving the candidate
  // stuck being told to "return to fullscreen" for something the popup
  // itself caused. A custom, in-page confirmation is just DOM content, not
  // a browser dialog, so it never touches fullscreen at all.
  const unansweredCount = questions.length - Object.keys(answers).length;
  const handleSubmitClick = () => {
    if (unansweredCount > 0) {
      setShowMissedConfirm(true);
      return;
    }
    handleSubmitAll();
  };
  const confirmSubmitAnyway = () => {
    setShowMissedConfirm(false);
    handleSubmitAll();
  };

  // Single timer driving both the visible countdown and auto-submit — when
  // deadlineEpoch is set, both are computed from that SAME wall-clock value
  // every tick (robust to tab throttling/backgrounding, unlike decrementing
  // a local counter), so the displayed time and the enforced deadline can
  // never diverge. Without a deadlineEpoch (legacy jobs with no configured
  // total test time), falls back to a plain local 60-minute countdown.
  useEffect(() => {
    const id = setInterval(() => {
      if (deadlineEpoch) {
        const remaining = Math.max(0, Math.round((deadlineEpoch - Date.now()) / 1000));
        setTimeLeft(remaining);
        if (remaining <= 0) handleSubmitAll();
      } else {
        setTimeLeft((t) => {
          if (t <= 1) { handleSubmitAll(); return 0; }
          return t - 1;
        });
      }
    }, 1000);
    return () => clearInterval(id);
  }, [handleSubmitAll, deadlineEpoch]);

  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [currentSet]);

  // Restore draft on mount
  useEffect(() => {
    if (!campaignId) return;
    const draftKey = `vagmee_mcq_${campaignId}`;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const { answers: saved, timeLeft: savedTime, savedAt, retakeGeneration, questionSetId, assessmentId } = JSON.parse(raw);
      if (questions[0]?.question_set_id && (questionSetId !== questions[0].question_set_id || assessmentId !== questions[0]?.assessment_id)) {
        localStorage.setItem(`${draftKey}_previous_version`, raw);
        return;
      }
      // Comparing against the draft's OWN recorded retake generation, not
      // just savedAt — a candidate whose browser tab stayed open (e.g. the
      // "dropped connection mid-test" case retakes exist for) keeps
      // autosaving the SAME stale answers every 10s, which pushes savedAt
      // forward in time even though nothing new was actually answered. That
      // can leave savedAt looking newer than the retake grant even though
      // the answers themselves are from before it — retakeGeneration is
      // stamped once per attempt and isn't affected by that, so it's the
      // reliable signal for "is this draft from the CURRENT attempt".
      if ((retakeGeneration || null) !== (minDraftTimestamp || null)) {
        // Either from before this retake was granted, or (for a draft saved
        // by an older build that never recorded a generation) unverifiable
        // — discard rather than risk restoring a previous, already-scored
        // attempt into what's supposed to be a fresh test.
        localStorage.removeItem(draftKey);
        return;
      }
      if (!saved || Date.now() - savedAt > 86400000) return;
      if (Object.keys(saved).length > 0) {
        setAnswers(saved);
        // When there's a deadlineEpoch, timeLeft is already correctly
        // recomputed from it (both in the initial state and every tick) —
        // don't override that with a possibly-stale saved value.
        if (!deadlineEpoch && savedTime > 60) setTimeLeft(savedTime);
        toast("Draft restored from your last session.", { icon: "📋", duration: 4000 });
      }
    } catch (_) {}
  }, []); // eslint-disable-line

  // Auto-save every 10 seconds
  useEffect(() => {
    if (!campaignId || submitted) return;
    const id = setInterval(() => {
      try {
        localStorage.setItem(`vagmee_mcq_${campaignId}`, JSON.stringify({
          answers, timeLeft, savedAt: Date.now(), retakeGeneration: minDraftTimestamp || null,
          questionSetId: questions[0]?.question_set_id,
          assessmentId: questions[0]?.assessment_id,
        }));
        setLastSavedAt(Date.now());
      } catch (_) {}
    }, 10000);
    return () => clearInterval(id);
  }, [answers, timeLeft, campaignId, submitted, minDraftTimestamp, questions]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  const currentSetQs = questions.slice(currentSet * 10, currentSet * 10 + 10);
  const answeredTotal = Object.keys(answers).length;

  const getSectProgress = (secId) => {
    const sq = questions.filter((q) => q.section === secId);
    return { total: sq.length, answered: sq.filter((q) => answers[q.id] !== undefined).length };
  };

  const toggleBookmark = (id) => {
    setBookmarks((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const setAnswer = (id, opt) => {
    setAnswers((prev) => ({ ...prev, [id]: opt }));
  };

  // Determine which section the current set is in
  const firstQInSet = currentSetQs[0];
  const currentSectId = firstQInSet?.section || "technical";

  return (
    <div className="atest-shell">
      {/* Offline banner */}
      {!isOnline && (
        <div style={{ background: "rgba(248,113,113,0.15)", borderBottom: "1px solid rgba(248,113,113,0.3)", padding: "0.55rem 1.25rem", fontSize: "0.82rem", color: "#f87171", display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 500 }}>
          <span>⚠</span> Connection lost — your answers are safely saved locally. They will be submitted when you reconnect.
        </div>
      )}

      {/* Top bar */}
      <div className="atest-top">
        <div className="atest-top__left">
          <span className="atest-top__brand">Assessment Test</span>
          <div className="atest-top__divider" />
          <span className="atest-top__set">Set {currentSet + 1} / {SETS}</span>
          <span className="atest-top__progress">{answeredTotal} / {questions.length} answered</span>
          {lastSavedAt && <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.28)", marginLeft: "0.35rem" }}>· saved</span>}
        </div>
        <div className={`atest-timer${timeLeft < 300 ? " atest-timer--warn" : ""}`}>
          <div className="atest-timer__dot" />
          {formatTime(timeLeft)}
        </div>
        {(!hideSubmitUntilLast || currentSet === SETS - 1) && (
          <button type="button" className="atest-submit-btn" onClick={handleSubmitClick} disabled={submitted}>
            {submitted ? "Submitting…" : "Submit All"}
          </button>
        )}
      </div>

      {/* Body */}
      <div className="atest-body">
        {/* Sidebar */}
        <div className="atest-sidebar">
          {/* Section nav */}
          <div className="atest-sect-nav">
            <div className="atest-sect-nav__label">Sections</div>
            {sectionsMeta.map((s) => {
              const prog = getSectProgress(s.id);
              const pct = prog.total > 0 ? Math.round((prog.answered / prog.total) * 100) : 0;
              const isActive = currentSectId === s.id;
              return (
                <div key={s.id} className={`atest-sn${isActive ? " is-on" : ""}`}>
                  <div className="atest-sn__ring" style={{ position: "relative", width: 38, height: 38, flexShrink: 0 }}>
                    <DonutRing pct={pct} color={s.color} size={38} stroke={3.5} />
                    <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center",
                      fontSize: "9px", fontWeight: 700, color: "rgba(255,255,255,0.7)", fontFamily: "monospace" }}>
                      {pct}%
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="atest-sn__name">{s.label}</div>
                    <div className="atest-sn__meta">{prog.answered}/{prog.total} done</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Question palette */}
          <div className="atest-palette">
            <div className="atest-palette__label">Questions</div>
            <div className="atest-palette__grid">
              {questions.map((q, idx) => {
                const setIdx = Math.floor(idx / 10);
                const answered = answers[q.id] !== undefined;
                const bookmarked = bookmarks.has(q.id);
                const isCurSet = setIdx === currentSet;
                return (
                  <button key={q.id} type="button"
                    className={`qdot-a${answered ? " answered" : ""}${isCurSet ? " current-set" : ""}${bookmarked ? " bookmarked" : ""}`}
                    onClick={() => setCurrentSet(setIdx)}
                    title={`Q${idx + 1}${answered ? " (answered)" : ""}${bookmarked ? " (bookmarked)" : ""}`}>
                    {idx + 1}
                  </button>
                );
              })}
            </div>
            <div className="atest-palette__legend">
              <span><i className="qdot-leg qdot-leg--answered" />Answered</span>
              <span><i className="qdot-leg qdot-leg--current" />Current</span>
              <span><i className="qdot-leg qdot-leg--bookmark" />Bookmarked</span>
              <span><i className="qdot-leg" />Pending</span>
            </div>
          </div>
        </div>

        {/* Main area */}
        <div className="atest-main" ref={mainRef}>
          <div className="atest-set-header">
            <span className="atest-set-header__sect" style={{ color: sectionMeta(currentSectId).color }}>
              {sectionMeta(currentSectId).label}
            </span>
            <span className="atest-set-header__title">Questions {currentSet * 10 + 1}–{Math.min(currentSet * 10 + 10, questions.length)}</span>
          </div>

          {currentSetQs.map((q, i) => (
            <QuestionCard key={q.id} q={q} qNum={currentSet * 10 + i + 1}
              answer={answers[q.id]}
              onAnswer={(opt) => setAnswer(q.id, opt)}
              bookmarked={bookmarks.has(q.id)}
              onBookmark={() => toggleBookmark(q.id)} />
          ))}

          {/* Set navigation */}
          <div className="atest-nav">
            <button type="button" className={`atest-nav__btn${currentSet === 0 ? " is-disabled" : ""}`}
              onClick={() => currentSet > 0 && setCurrentSet((s) => s - 1)}
              disabled={currentSet === 0}>
              ← Previous Set
            </button>
            {currentSet < SETS - 1 ? (
              <button type="button" className="atest-nav__btn is-primary"
                onClick={() => setCurrentSet((s) => s + 1)}>
                Next Set →
              </button>
            ) : (
              <button type="button" className="atest-nav__btn is-submit" onClick={handleSubmitClick} disabled={submitted}>
                {submitted ? "Submitting…" : "Submit Test ✓"}
              </button>
            )}
          </div>
        </div>
      </div>

      {showMissedConfirm && (
        <div className="atest-confirm-overlay">
          <div className="atest-confirm-card">
            <h3>Unanswered questions</h3>
            <p>
              You have {unansweredCount} unanswered question{unansweredCount === 1 ? "" : "s"} out of {questions.length}.
              Submit anyway? You can go back and answer them first.
            </p>
            <div className="atest-confirm-actions">
              <button type="button" className="atest-confirm-btn is-secondary" onClick={() => setShowMissedConfirm(false)}>
                Go back
              </button>
              <button type="button" className="atest-confirm-btn is-primary" onClick={confirmSubmitAnyway}>
                Submit anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Phase 4: Scorecard ────────────────────────────────────────────────────────

function ScoreBigRing({ pct, color, size = 140, stroke = 10 }) {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(100, Math.max(0, pct)) / 100);
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={stroke}
        strokeDasharray={`${circ}`} strokeDashoffset={`${offset}`}
        strokeLinecap="round"
        style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
      />
    </svg>
  );
}

function getGrade(pct) {
  if (pct >= 85) return { label: "Excellent", color: "#0e7c66" };
  if (pct >= 70) return { label: "Good",      color: "#667eea" };
  if (pct >= 50) return { label: "Average",   color: "#c99a2e" };
  return          { label: "Needs work",   color: "#c0364b" };
}

export function AssessmentScorecard({ questions, answers, serverResult, config, timeUsed, onRetake, showReview = true, showRetake = true }) {
  const totalCorrect = serverResult.correct_answers;
  const totalPct = serverResult.scores.total;
  const grade = getGrade(totalPct);

  const sectionScores = SECTIONS_META.map((s) => {
    const sq = questions.filter((q) => q.section === s.id);
    const correct = serverResult.sections[s.id]?.correct || 0;
    const attempted = sq.filter((q) => answers[q.id] !== undefined).length;
    const pct = serverResult.scores[s.id] || 0;
    return { ...s, total: sq.length, correct, attempted, pct };
  });

  const minutesTaken = timeUsed ? Math.floor(timeUsed / 60000) : 0;
  const secsTaken = timeUsed ? Math.floor((timeUsed % 60000) / 1000) : 0;

  return (
    <div className="ascore-container">
      {/* Hero */}
      <div className="ascore-hero">
        <div className="ascore-hero__left">
          <div style={{ position: "relative", width: 140, height: 140 }}>
            <ScoreBigRing pct={totalPct} color={grade.color} />
            <div className="ascore-hero__donut-center">
              <span className="ascore-hero__pct">{totalPct}<small>%</small></span>
              <span className="ascore-hero__grade" style={{ color: grade.color }}>{grade.label}</span>
            </div>
          </div>
        </div>
        <div className="ascore-hero__right">
          <div className="ascore-hero__kicker">Assessment complete</div>
          <h2 className="ascore-hero__title">Your results</h2>
          {config?.role && <p className="ascore-hero__sub">Role: <strong>{config.role}</strong></p>}
          <div className="ascore-hero__stats">
            <div className="ascore-stat">
              <span className="ascore-stat__val">{totalCorrect}<small>/{questions.length}</small></span>
              <span className="ascore-stat__label">Correct</span>
            </div>
            <div className="ascore-stat">
              <span className="ascore-stat__val">{questions.length - Object.keys(answers).length}</span>
              <span className="ascore-stat__label">Skipped</span>
            </div>
            <div className="ascore-stat">
              <span className="ascore-stat__val">{minutesTaken}<small>m {secsTaken}s</small></span>
              <span className="ascore-stat__label">Time taken</span>
            </div>
          </div>
          {showRetake && (
            <div style={{ display: "flex", gap: "0.65rem", marginTop: "1rem" }}>
              <button type="button" className="setup-start-btn" onClick={onRetake}
                style={{ padding: "0.6rem 1.2rem", fontSize: "0.82rem" }}>
                Retake Test
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Section score cards */}
      <div className="ascore-strip">
        {sectionScores.map((s) => (
          <div key={s.id} className="ascore-card">
            <div className="ascore-card__ring" style={{ position: "relative" }}>
              <ScoreBigRing pct={s.pct} color={s.color} size={76} stroke={6} />
              <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center",
                fontSize: "14px", fontWeight: 700, color: "rgba(255,255,255,0.88)" }}>
                {s.pct}%
              </div>
            </div>
            <div className="ascore-card__body">
              <span className="ascore-card__label" style={{ color: s.color }}>{s.label}</span>
              <span className="ascore-card__val">{s.correct}<small>/{s.total}</small></span>
              <span className="ascore-card__sub">{s.attempted} attempted</span>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed breakdown */}
      <div className="ascore-breakdown">
        <div className="ascore-breakdown__head">Section breakdown</div>
        {sectionScores.map((s) => (
          <div key={s.id} className="ascore-row">
            <div className="ascore-row__icon" style={{ background: s.colorDim, color: s.color, border: `1px solid ${s.color}44` }}>
              {SECTION_ICONS[s.id]}
            </div>
            <div className="ascore-row__body">
              <div className="ascore-row__name">{s.label}</div>
              <div className="ascore-row__bar">
                <div className="ascore-row__bar-fill" style={{ width: `${s.pct}%`, background: s.color }} />
              </div>
            </div>
            <div className="ascore-row__right">
              <span className="ascore-row__score">{s.correct}/{s.total}</span>
              <span className="ascore-row__pct" style={{ color: s.color }}>{s.pct}%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Q&A review — practice mode only */}
      {showReview && <ReviewSection questions={questions} answers={answers} correctness={serverResult.correctness} />}
    </div>
  );
}

function ReviewSection({ questions, answers, correctness }) {
  const [open, setOpen] = useState(false);
  const wrong = questions.filter((q) => answers[q.id] !== undefined && !correctness[String(q.id)]);
  const skipped = questions.filter((q) => answers[q.id] === undefined);

  return (
    <div className="ascore-review">
      <button type="button" className="ascore-review__toggle" onClick={() => setOpen((o) => !o)}>
        <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.7" width="15" height="15">
          <circle cx="9" cy="9" r="7" /><path d="M9 8v4M9 6h.01" strokeLinecap="round" />
        </svg>
        Review incorrect answers ({wrong.length} wrong, {skipped.length} skipped)
        <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.7" width="14" height="14"
          style={{ marginLeft: "auto", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
          <path d="M4 7l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="ascore-review__body">
          {wrong.length === 0 && skipped.length === 0 && (
            <p style={{ color: "#0e7c66", fontWeight: 600, padding: "1rem 0" }}>Perfect score — no incorrect answers!</p>
          )}
          {wrong.map((q) => {
            const secMeta = SECTIONS_META.find((s) => s.id === q.section);
            return (
              <div key={q.id} className="ascore-review__q">
                <div className="ascore-review__q-head">
                  <span className="atest-q__num">Q{q.id}</span>
                  {secMeta && <span className="atest-q__sect" style={{ color: secMeta.color, background: secMeta.colorDim, border: `1px solid ${secMeta.color}55` }}>{secMeta.label}</span>}
                </div>
                <p className="ascore-review__q-text">{q.text}</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                  {["A","B","C","D"].map((k) => (
                    <div key={k} className={`ascore-review__opt${k === answers[q.id] ? " is-wrong" : ""}`}>
                      <span className="atest-opt__key">{k}</span>
                      <span>{q.options[k]}</span>
                      {k === answers[q.id] && <span className="ascore-review__badge ascore-review__badge--wrong">✗ Your answer</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Submission overlay screens ────────────────────────────────────────────────

const SUBMIT_STEPS = [
  { key: "saving",    label: "Saving answers" },
  { key: "uploading", label: "Uploading submission" },
  { key: "processing",label: "Processing" },
  { key: "completed", label: "Completed" },
];

function SubmittingScreen({ step = "saving" }) {
  const isRetrying = step === "retrying";
  const currentIdx = SUBMIT_STEPS.findIndex(s => s.key === step);
  return (
    <div className="atest-generating">
      <div className="atest-generating__inner">
        {!isRetrying && (
          <div className="atest-generating__ring">
            {[0, 1, 2].map((i) => (
              <div key={i} className="atest-generating__dot" style={{ animationDelay: `${i * 0.22}s` }} />
            ))}
          </div>
        )}
        {isRetrying && <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>🔄</div>}
        <p className="atest-generating__title">{isRetrying ? "Connection issue — retrying…" : "Submitting Assessment…"}</p>
        <p className="atest-generating__msg">Please do not close this tab.</p>
        {!isRetrying && (
          <div style={{ marginTop: "1.25rem", display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-start", minWidth: 220 }}>
            {SUBMIT_STEPS.map((s, idx) => {
              const done    = idx < currentIdx;
              const active  = idx === currentIdx;
              return (
                <div key={s.key} style={{ display: "flex", alignItems: "center", gap: "0.6rem", opacity: (done || active) ? 1 : 0.3 }}>
                  <span style={{ fontSize: "0.85rem", width: 18, textAlign: "center", color: done ? "#36c8a0" : active ? "#a78bfa" : "rgba(255,255,255,0.4)" }}>
                    {done ? "✓" : active ? "▶" : "○"}
                  </span>
                  <span style={{ fontSize: "0.82rem", color: done ? "#36c8a0" : active ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.35)", fontWeight: active ? 600 : 400 }}>
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {isRetrying && <p className="atest-generating__hint">Retrying automatically…</p>}
        {!isRetrying && <p className="atest-generating__hint">This may take up to 1 minute during peak usage.</p>}
      </div>
    </div>
  );
}

function SubmitErrorScreen({ error, onRetry }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", gap:"1rem", padding:"2rem", textAlign:"center" }}>
      <div style={{ fontSize:"2rem" }}>⚠️</div>
      <h2 style={{ margin:0, fontSize:"1.2rem" }}>Submission Failed</h2>
      <p style={{ margin:0, color:"rgba(255,255,255,0.5)", fontSize:"0.9rem", maxWidth:"400px" }}>{error}</p>
      <p style={{ margin:0, color:"rgba(255,255,255,0.4)", fontSize:"0.82rem", maxWidth:"400px" }}>Your answers are preserved. Click below to try again.</p>
      <button onClick={onRetry} style={{ marginTop:"0.5rem", padding:"0.65rem 1.5rem", background:"linear-gradient(135deg,#7c6ef2,#a78bfa)", color:"#fff", border:"none", borderRadius:"0.55rem", fontWeight:700, cursor:"pointer", fontSize:"0.9rem" }}>
        Retry Submission
      </button>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function AssessmentScreen({
  prefilledConfig = null,
  prefilledQuestions = null,
  campaignId = null,
  onTestStart = null,
  onComplete = null,
  skipScorecard = false,
  deadlineEpoch = null,
}) {
  // Skip config UI entirely when launched from a campaign
  const [phase, setPhase] = useState(prefilledConfig ? "generating" : "config");
  const [assessConfig, setAssessConfig] = useState(prefilledConfig || null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [campaignError, setCampaignError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitStep, setSubmitStep] = useState("saving");
  const [serverResult, setServerResult] = useState(null);
  const campaignStarted = useRef(false);
  const submittingRef = useRef(false);
  const { isOnline } = useSubmissionSafety();

  // Retry-aware token fetch — Firebase may take a moment to restore after a login redirect
  const getTokenWithRetry = useCallback(async () => {
    for (let i = 0; i < 6; i++) {
      const token = await getAuthToken();
      if (token) return token;
      await new Promise((r) => setTimeout(r, 400));
    }
    return null;
  }, []);

  // Campaign mode: always serve from the pre-generated question cache stored at
  // campaign-creation time. Never generate per-candidate — that would use a
  // different model and create a different set for every user on the same link.
  useEffect(() => {
    if (!prefilledConfig || campaignStarted.current) return;
    campaignStarted.current = true;

    if (prefilledQuestions && prefilledQuestions.length >= 10) {
      setQuestions(prefilledQuestions);
      setStartTime(Date.now());
      setPhase("test");
      onTestStart?.();
    } else {
      // Questions were not pre-generated at campaign creation (or generation failed).
      // Do NOT generate per-candidate — show an error so the coordinator can fix it.
      setCampaignError(
        "The question set for this campaign is not ready yet. " +
        "Please contact your coordinator or wait a few minutes and reload."
      );
      setPhase("campaign_error");
    }
  }, []); // eslint-disable-line

  // On mount in campaign mode: if a submit marker exists from a prior interrupted
  // attempt, warn the candidate — answers are in sessionStorage for reference.
  useEffect(() => {
    if (!campaignId) return;
    try {
      const raw = sessionStorage.getItem(`asubmit_${campaignId}`);
      if (!raw) return;
      const { ts } = JSON.parse(raw);
      if (Date.now() - ts < 3600000) {
        toast("A previous submission may have been interrupted. If your results are missing, contact your coordinator.", { duration: 8000 });
      } else {
        sessionStorage.removeItem(`asubmit_${campaignId}`);
      }
    } catch (_) {}
  }, []); // eslint-disable-line

  const handleStart = async (cfg) => {
    setAssessConfig(cfg);
    setPhase("generating");
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Authentication failed. Please log in again.");
      const res = await fetch(`${API_BASE}/api/assessment/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          role: cfg.role,
          experience: cfg.experience,
          difficulty: cfg.difficulty,
          category: cfg.category,
          company: cfg.company,
          jd_text: cfg.jdText,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.detail || `Server error ${res.status}`);
      }
      const data = await res.json();
      if (data.error || !data.questions || data.questions.length < 10) {
        throw new Error(data.error || "Not enough questions generated. Please try again.");
      }
      setQuestions(data.questions);
      setStartTime(Date.now());
      setPhase("test");
    } catch (err) {
      toast.error(err.message || "Failed to generate assessment. Please try again.");
      setPhase("config");
    }
  };

  const handleSubmit = useCallback(async (finalAnswers, _retryCount = 0) => {
    if (_retryCount === 0 && submittingRef.current) return;
    submittingRef.current = true;

    const now = Date.now();
    const cfg = assessConfig || {};
    const submitPayload = {
      role:            cfg.role || "",
      experience:      cfg.experience || "0",
      difficulty:      cfg.difficulty || "",
      category:        cfg.category || "generic",
      company:         cfg.company || "",
      question_set_id: questions[0]?.question_set_id,
      assessment_id: questions[0]?.assessment_id,
      time_used_ms:    startTime ? now - startTime : 0,
      campaign_id:     campaignId || null,
      answers:         questions.map((q) => ({
        question_id: q.id,
        selected:    finalAnswers[q.id] || null,
      })),
    };

    const storageKey = `asubmit_${campaignId || "practice"}`;
    const draftKey   = `vagmee_mcq_${campaignId || "practice"}`;
    try { sessionStorage.setItem(storageKey, JSON.stringify({ ts: now, retryCount: _retryCount })); } catch (_) {}

    setAnswers(finalAnswers);
    setEndTime(now);
    setSubmitStep("saving");
    setPhase("submitting");
    console.log(`[Assessment] submission_start retry=${_retryCount} answers=${Object.keys(finalAnswers).length}`);

    try {
      await new Promise(r => setTimeout(r, 300)); // brief "saving" display
      setSubmitStep("uploading");

      const token = await getTokenWithRetry();
      if (!token) throw new Error("Authentication failed. Please refresh and try again.");

      const controller = new AbortController();
      const timeoutId  = setTimeout(() => controller.abort(), 120000);

      const res = await fetch(`${API_BASE}/api/assessment/submit`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        signal:  controller.signal,
        body:    JSON.stringify(submitPayload),
        // Grading already happens server-side, synchronously, as part of
        // handling this request — keepalive just makes sure the browser
        // still delivers it even if the candidate closes the tab right
        // after clicking Submit, instead of aborting it mid-flight.
        keepalive: true,
      });
      clearTimeout(timeoutId);

      setSubmitStep("processing");
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || e.message || `Server error ${res.status}`);
      }

      const graded = await res.json();
      if (graded.status !== 'saved' || !graded.scores || !graded.sections || !graded.correctness) {
        throw new Error('The server could not confirm your assessment score. Your answers are preserved.');
      }
      setServerResult(graded);
      setSubmitStep("completed");
      // Clear saved draft — no longer needed
      try { sessionStorage.removeItem(storageKey); localStorage.removeItem(draftKey); } catch (_) {}
      console.log(`[Assessment] submission_completed duration_ms=${Date.now() - now} retry=${_retryCount}`);

      await new Promise(r => setTimeout(r, 400)); // let user see "Completed"
      if (skipScorecard) {
        onComplete?.({ totalCorrect: graded.correct_answers, scorePercent: graded.scores.total, totalQuestions: graded.total_questions });
      } else {
        setPhase("scorecard");
        onComplete?.();
      }

    } catch (err) {
      const errMsg = err.name === "AbortError"
        ? "Request timed out after 2 minutes. Your answers are saved — please try again."
        : (err.message || "Submission failed. Your answers are saved.");

      if (_retryCount === 0) {
        // Auto-retry once after 3 seconds
        console.warn(`[Assessment] submission_failed retry=0 — auto-retrying in 3s`);
        setSubmitStep("retrying");
        submittingRef.current = false;
        await new Promise(r => setTimeout(r, 3000));
        return handleSubmit(finalAnswers, 1);
      }

      // Second failure — emergency local save + show error
      try {
        localStorage.setItem(`vagmee_emergency_${campaignId || "practice"}`, JSON.stringify({
          payload: submitPayload, savedAt: Date.now(),
        }));
      } catch (_) {}
      console.error(`[Assessment] submission_failed_final duration_ms=${Date.now() - now} error=${errMsg}`);
      submittingRef.current = false;
      setSubmitError(errMsg);
      setPhase("submit_error");
    }
  }, [questions, assessConfig, startTime, campaignId, skipScorecard, onComplete, getTokenWithRetry]);

  const handleSubmitRetry = () => {
    submittingRef.current = false;
    setSubmitError(null);
    handleSubmit(answers, 0);
  };

  const handleRetake = () => {
    submittingRef.current = false;
    setSubmitError(null);
    setQuestions([]);
    setAnswers({});
    setStartTime(null);
    setEndTime(null);
    setPhase("config");
  };

  const handleCampaignRetry = () => {
    setCampaignError(null);
    campaignStarted.current = false;
    setPhase("generating");
    // Re-trigger generation via a state trick — force remount would be cleaner,
    // but here we reset the guard and let the parent remount if needed.
    // For simplicity, reload the page with the same URL.
    window.location.reload();
  };

  if (phase === "config")         return <AssessmentConfig onStart={handleStart} />;
  if (phase === "generating")     return <GeneratingScreen />;
  if (phase === "submitting")     return <SubmittingScreen step={submitStep} />;
  if (phase === "submit_error")   return <SubmitErrorScreen error={submitError} onRetry={handleSubmitRetry} />;
  if (phase === "campaign_error") return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", gap:"1rem", padding:"2rem", textAlign:"center" }}>
      <div style={{ fontSize:"2rem" }}>⚠️</div>
      <h2 style={{ margin:0, fontSize:"1.2rem" }}>Could not load your assessment</h2>
      <p style={{ margin:0, color:"rgba(255,255,255,0.5)", fontSize:"0.9rem", maxWidth:"400px" }}>{campaignError}</p>
      <button onClick={handleCampaignRetry} style={{ marginTop:"0.5rem", padding:"0.65rem 1.5rem", background:"linear-gradient(135deg,#7c6ef2,#a78bfa)", color:"#fff", border:"none", borderRadius:"0.55rem", fontWeight:700, cursor:"pointer", fontSize:"0.9rem" }}>
        Try Again
      </button>
    </div>
  );
  if (phase === "test")           return <AssessmentTest questions={questions} onSubmit={handleSubmit} campaignId={campaignId} isOnline={isOnline} deadlineEpoch={deadlineEpoch} />;
  if (phase === "scorecard")      return (
    <AssessmentScorecard
      questions={questions}
      answers={answers}
      serverResult={serverResult}
      config={assessConfig}
      timeUsed={endTime && startTime ? endTime - startTime : null}
      onRetake={handleRetake}
      showReview={!campaignId}
      showRetake={!campaignId}
    />
  );
  return null;
}
